// src/views/kiddo-games/memory/KiddoMemoryView.tsx
// Memory Easy — kopia src/views/memory/MemoryView.tsx, rozwijana osobno.
// Różnice: pary dźwięk–obrazek bez napisów (karta z dźwiękiem zamiast karty ze słowem; słowo wypowiada tylko
// ona, obrazek jest bez dźwięku), własne style i karta, następna gra z grupy Easy, bez nieużywanego podwójnego
// kliknięcia karty, plansze zawsze po 4 pary (ostatnią dopełniają słowa z poprzednich plansz), po znalezieniu
// pary polskie tłumaczenie z przełącznikiem polskiego lektora.
import React, {
  useCallback, useEffect, useState, useRef, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import { gsap } from 'gsap';
import classes from './KiddoMemoryView.module.less';
import FixWidthContent from '../../../components/FixWidthContent';
import { Card } from './Card';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import { Roles } from '../../../constants/Roles';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import { BASE_API_URL } from '../../../services/ApiService';
import withTagManager from '../../../hooks/withTagManager';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { usePolishAudio } from '../../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../../components/PolishAudioButton/PolishAudioButton';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

interface CardData {
  wordId: string;
  cardId: string;
  term: string;
  definition: string;
  image: string;
  type: 'sound' | 'image';
  isFlipped: boolean;
  isFinished: boolean;
}

const MAX_WORDS_PER_PART = 4; // 4 słowa = 8 kart na planszy
const MATCH_DELAY = 1200;
const NEXT_PART_DELAY = 1500;
const POLISH_AUDIO_DELAY = 200; // ms – przerwa między angielskim a polskim słowem
const READING_TIMEOUT = 10000; // ms – bezpiecznik, gdy lektor nie zgłosi końca czytania

// Funkcja do tasowania array (Fisher-Yates)
const shuffleArray = <T extends unknown>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

const divideWordsIntoParts = (words: any[]): any[] => {
  // Tasuj słowa PRZED podziałem na części
  const shuffledWords = shuffleArray(words);
  const wordsCount = shuffledWords.length;

  // Jeśli <= 4 słowa (8 kart), nie dziel — przy 1–3 słowach nie ma z czego ułożyć 4 różnych par
  if (wordsCount <= MAX_WORDS_PER_PART) {
    return [shuffledWords];
  }

  // Każda część ma 4 pary — słowa idą czwórkami
  const dividedWords: any[] = [];
  for (let i = 0; i < wordsCount; i += MAX_WORDS_PER_PART) {
    dividedWords.push(shuffledWords.slice(i, i + MAX_WORDS_PER_PART));
  }

  // Ostatnią część dopełnij losowymi słowami z poprzednich części (najwyżej 3 powtórki).
  // To samo słowo nie może trafić dwa razy na jedną planszę
  const lastPart = dividedWords[dividedWords.length - 1];
  const missingCount = MAX_WORDS_PER_PART - lastPart.length;
  if (missingCount > 0) {
    const shownWords = shuffledWords.slice(0, wordsCount - lastPart.length);
    lastPart.push(...shuffleArray(shownWords).slice(0, missingCount));
  }

  return dividedWords;
};

const generateCardsForPart = (part: any[]): CardData[] => {
  const cards: CardData[] = [];

  part.forEach((word: any) => {
    cards.push({
      wordId: word.id,
      cardId: nanoid(),
      term: word.term,
      definition: word.definition,
      image: word.image,
      type: 'sound',
      isFlipped: false,
      isFinished: false,
    });

    cards.push({
      wordId: word.id,
      cardId: nanoid(),
      term: word.term,
      definition: word.definition,
      image: word.image,
      type: 'image',
      isFlipped: false,
      isFinished: false,
    });
  });

  // Tasuj karty w ramach części
  return shuffleArray(cards);
};

function KiddoMemoryView() {
  const activeList = useSelector(getActiveList);
  const goToNext = useGoToRoute(Path.KIDDO_PICK);
  const { play: playAudio, stop: stopAudio } = useAudioPlayer();
  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'kiddo_memory_playPolish' });

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.MEMORY_EASY);

  // State
  const [activePartIndex, setActivePartIndex] = useState<number>(0);
  const [dividedWords, setDividedWords] = useState<any[]>([]);
  const [cards, setCardsList] = useState<CardData[]>([]);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [listEndInfo, setListEndInfo] = useState<boolean>(false);
  const [isInteractionDisabled, setIsInteractionDisabled] = useState<boolean>(false);
  const [partKey, setPartKey] = useState<string>(nanoid()); // Unikalny klucz dla każdej partii
  const [matchedCard, setMatchedCard] = useState<CardData | null>(null); // znaleziona para, czeka na lektora

  // Refs do zarządzania timerami i animacjami
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const cardsGridRef = useRef<HTMLDivElement>(null);

  // Koniec angielskiego słowa z ostatnio odwróconej karty z głośnikiem — polskie tłumaczenie na nie nie wchodzi
  const englishEndedRef = useRef<Promise<void>>(Promise.resolve());

  // Przełącznik PL z chwili znalezienia pary — przełączenie w trakcie czytania nie zaczyna go od nowa
  const playPolishRef = useRef(playPolish);
  playPolishRef.current = playPolish;

  // Utility do dodawania tracked timerów
  const addTimer = useCallback((callback: () => void, delay: number) => {
    const timer = setTimeout(() => {
      callback();
      // Usuń z listy po wykonaniu
      timersRef.current = timersRef.current.filter((t) => t !== timer);
    }, delay);
    timersRef.current.push(timer);

    return timer;
  }, []);

  // Cleanup wszystkich timerów
  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  // Cleanup przy unmount
  useEffect(() => () => {
    clearAllTimers();
    stopAudio();
  }, [clearAllTimers, stopAudio]);

  // Funkcja do czyszczenia aktualnego matcha (używa functional update)
  const clearActualMatch = useCallback(() => {
    setCardsList((prevCards) => prevCards.map((card) => ({
      ...card,
      isFlipped: false,
    })));
    setIsInteractionDisabled(false);
  }, []);

  // Handler kliknięcia karty
  const handleClick = useCallback((cardId: string) => {
    setCardsList((prevCards) => {
      // Sprawdź czy karta już flipped lub finished
      const currentCard = prevCards.find((card) => card.cardId === cardId);
      if (!currentCard || currentCard.isFlipped || currentCard.isFinished) {
        return prevCards;
      }

      // Sprawdź ile kart jest już flipped (bez tej właśnie klikniętej)
      const alreadyFlipped = prevCards.filter((card) => card.isFlipped);

      // Jeśli już są 2 karty flipped, zignoruj kliknięcie
      if (alreadyFlipped.length >= 2) {
        return prevCards;
      }

      // Wyczyść poprzednie timery
      clearAllTimers();

      // Słowo wypowiada tylko karta z dźwiękiem — nie ma napisu, więc dźwięk to jej jedyna treść.
      // Obrazek jest bez dźwięku
      if (currentCard.type === 'sound') {
        const cleanTerm = cleanTextForTTS(currentCard.term);
        const audioUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;
        englishEndedRef.current = new Promise((resolve) => {
          playAudio(audioUrl, () => resolve()).catch((err) => console.warn('Audio playback failed:', err));
        });
      }

      // Flip aktualną kartę
      const newCards = prevCards.map((card) => (card.cardId === cardId
        ? { ...card, isFlipped: true }
        : card));

      // Sprawdź stan po flip
      const flippedCards = newCards.filter((card) => card.isFlipped);

      if (flippedCards.length === 2) {
        // Dwie karty są flipped
        const [first, second] = flippedCards;

        if (first.wordId === second.wordId) {
          // MATCH! ✅ Para zostaje odkryta, dopóki lektor nie przeczyta polskiego tłumaczenia
          setIsInteractionDisabled(true);
          setMatchedCard(first);
        } else {
          // NIE PASUJĄ ❌
          setIsInteractionDisabled(true);
          addTimer(clearActualMatch, MATCH_DELAY);
        }
      }

      return newCards;
    });
  }, [addTimer, clearActualMatch, clearAllTimers, playAudio]);

  // Próba to odkrycie drugiej karty: para albo nie. Rozstrzyga się w aktualizacji stanu kart (handleClick), której
  // React może użyć więcej niż raz — dlatego próbę zapisuje efekt, raz na każdą parę odkrytych kart
  const recordedPairRef = useRef<string | null>(null);

  useEffect(() => {
    const flippedCards = cards.filter((card) => card.isFlipped);

    if (flippedCards.length !== 2) {
      recordedPairRef.current = null;

      return;
    }

    const [first, second] = flippedCards;
    const pairKey = `${first.cardId}:${second.cardId}`;

    if (recordedPairRef.current === pairKey) return;

    recordedPairRef.current = pairKey;
    recordAnswer(first.wordId === second.wordId);
  }, [cards]);

  // Znaleziona para: po angielskim słowie z karty z głośnikiem lektor czyta polskie tłumaczenie (przy włączonym
  // przełączniku PL). Para przechodzi w odnalezione, gdy minie MATCH_DELAY i lektor skończy — do tego czasu karty
  // nie reagują, żeby angielskie słowo z następnej karty nie weszło na polskie
  useEffect(() => {
    if (!matchedCard) return undefined;

    let finished = false;
    let delayPassed = false;
    let readingDone = !playPolishRef.current;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const finishPair = () => {
      if (finished || !delayPassed || !readingDone) return;
      finished = true;

      setCardsList((currentCards) => {
        const updatedCards = currentCards.map((card) => (card.wordId === matchedCard.wordId
          ? { ...card, isFlipped: false, isFinished: true }
          : card));

        // Sprawdź czy to była ostatnia para
        const notFinished = updatedCards.filter((card) => !card.isFinished);
        if (notFinished.length === 0) {
          setIsFinished(true);
        }

        return updatedCards;
      });
      setIsInteractionDisabled(false);
      setMatchedCard(null);
    };

    const finishReading = () => {
      readingDone = true;
      finishPair();
    };

    timers.push(setTimeout(() => {
      delayPassed = true;
      finishPair();
    }, MATCH_DELAY));

    if (!readingDone) {
      timers.push(setTimeout(finishReading, READING_TIMEOUT));

      englishEndedRef.current.then(() => {
        if (finished) return;
        timers.push(setTimeout(() => {
          if (!finished) playPolishAudio(matchedCard.definition, finishReading);
        }, POLISH_AUDIO_DELAY));
      });
    }

    return () => {
      finished = true;
      timers.forEach(clearTimeout);
      stopPolishAudio();
    };
  }, [matchedCard, playPolishAudio, stopPolishAudio]);

  // Inicjalizacja gry przy załadowaniu
  useEffect(() => {
    if (!activeList?.words?.length) return;

    const { words } = activeList;

    // Zawsze używaj divideWordsIntoParts - funkcja sama zdecyduje czy dzielić
    const divided = divideWordsIntoParts(words);
    setDividedWords(divided);
    const firstCards = generateCardsForPart(divided[0]);
    setCardsList(firstCards);

    setActivePartIndex(0);
    setIsFinished(false);
    setListEndInfo(false);
    setPartKey(nanoid());
  }, [activeList?.words]);

  // Obsługa przejścia do następnej części lub końca gry
  useEffect(() => {
    if (!isFinished) return;

    if (activePartIndex < dividedWords.length - 1) {
      // Przejdź do następnej części
      addTimer(() => {
        setIsFinished(false);
        const newIndex = activePartIndex + 1;
        setActivePartIndex(newIndex);
        const newPart = dividedWords[newIndex];
        const newCards = generateCardsForPart(newPart);
        setCardsList(newCards);
        setPartKey(nanoid()); // Nowy klucz dla nowej partii
      }, NEXT_PART_DELAY);
    } else {
      // Koniec wszystkich części
      addTimer(() => {
        setListEndInfo(true);
      }, NEXT_PART_DELAY);
    }
  }, [isFinished, activePartIndex, dividedWords, addTimer]);

  // 🎨 GSAP Animation - Magnetic Gather
  useEffect(() => {
    if (
      cards.length > 0
      && !listEndInfo
      && !isFinished
      && cardsGridRef.current
    ) {
      // Poczekaj na pełne wyrenderowanie DOM
      const animationTimer = setTimeout(() => {
        const cardElements = cardsGridRef.current?.children;
        if (cardElements && cardElements.length > 0) {
          gsap.fromTo(
            Array.from(cardElements),
            {
              opacity: 0,
              scale: 0.2,
              x: () => gsap.utils.random(-400, 400),
              y: () => gsap.utils.random(-300, 300),
              rotation: () => gsap.utils.random(-180, 180),
            },
            {
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
              rotation: 0,
              duration: 1,
              ease: 'expo.out',
              stagger: {
                amount: 0.4,
                from: 'random',
              },
            },
          );
        }
      }, 100);

      return () => clearTimeout(animationTimer);
    }

    return undefined;
  }, [partKey, listEndInfo, isFinished]);

  // Funkcja restart gry
  const repeatGame = useCallback(() => {
    clearAllTimers();
    stopAudio();

    setIsFinished(false);
    setListEndInfo(false);
    setActivePartIndex(0);
    setIsInteractionDisabled(false);
    setMatchedCard(null);

    // Wygeneruj nowe rundy z NOWYM tasowaniem!
    const { words } = activeList;
    const newDividedWords = divideWordsIntoParts(words); // Nowe tasowanie i inteligentny podział!
    setDividedWords(newDividedWords);
    const firstCards = generateCardsForPart(newDividedWords[0]);
    setCardsList(firstCards);

    setPartKey(nanoid()); // Nowy klucz dla restartu
  }, [activeList?.words, clearAllTimers, stopAudio]);

  // Memoizuj karty do renderowania
  const cardsToRender = useMemo(() => cards, [cards]);

  // Kolumn tyle, ile par na planszy (2–4), żeby karty leżały w dwóch równych rzędach (8 kart: 2×4, 6 kart: 2×3).
  // Odnalezione pary zostają na planszy, więc w trakcie gry liczba kolumn się nie zmienia
  const columns = Math.min(Math.max(cards.length / 2, 2), 4);
  const columnsClass = classes[`columns${columns}`];

  return (
    <>
      <FixWidthContent className={`${classes.main} ${listEndInfo ? '' : classes.boardCentered}`}>
        {dividedWords.length >= 2 && (
          <p className={classes.pages}>
            {activePartIndex + 1} / {dividedWords.length}
          </p>
        )}

        {!listEndInfo && (
          <div>
            <div className={`${classes.cardsGrid} ${columnsClass}`} ref={cardsGridRef}>
              {cardsToRender.map((card: CardData) => (
                <Card
                  id={card.cardId}
                  text={card.term}
                  image={card.image}
                  type={card.type}
                  isFinished={card.isFinished}
                  isFlipped={card.isFlipped}
                  isDisabled={isInteractionDisabled}
                  handleClick={handleClick}
                  key={card.cardId}
                />
              ))}
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} kiddo />
        )}

        {!listEndInfo && <UnsplashCredits words={dividedWords[activePartIndex]} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
}

export default withTagManager(KiddoMemoryView);
